import React from "react";
import { Checkbox, Switch } from "@panda0/panda0-ui";
import { Eye, Info } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";

import { FormData } from "../types";

const ProctoringSettingsEditor: React.FC = () => {
  const { control, watch } = useFormContext<FormData>();
  const isEnabled = watch("proctoringSettings.PROCTORING_ENABLED") || false;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Configure proctoring options to monitor candidates during panda quiz.
      </p>

      <div className="flex items-center justify-between mb-4">
        <label className="font-medium">Enable Proctoring</label>
        <Controller
          name="proctoringSettings.PROCTORING_ENABLED"
          control={control}
          render={({ field }) => (
            <Switch
              checked={field.value || false}
              fn={(checked) => field.onChange(checked)}
            />
          )}
        />
      </div>

      {isEnabled && (
        <div className="space-y-6">
          <div className="p-4 border rounded-md bg-gray-50">
            <div className="flex items-center mb-4">
              <Eye className="h-5 w-5 mr-2 text-primary" />
              <h3 className="font-medium">Monitoring Features</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_BROWSER_FOCUS"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Browser focus
                  </label>
                  <p className="text-xs text-gray-500">Track browser focus</p>
                </div>
              </div>

              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_BLOCK_SCREEN_SHARING"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Screen sharing
                  </label>
                  <p className="text-xs text-gray-500">Block screen sharing</p>
                </div>
              </div>

              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_MULTIFACE_DETECTION"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Multiple Face Detection
                  </label>
                  <p className="text-xs text-gray-500">
                    Detect if more than one person is present
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_VOICE_MONITORING"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Voice Monitoring
                  </label>
                  <p className="text-xs text-gray-500">
                    Detect vocal conversations during panda quiz
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_EYE_TRACKING"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Eye/Face Tracking
                  </label>
                  <p className="text-xs text-gray-500">
                    Track eye and face movements
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_LIVENESS_ENABLED"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Face Liveness Detection
                  </label>
                  <p className="text-xs text-gray-500">
                    Verify that a real person is taking the panda quiz
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_OBJECT_DETECTION"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Object Detection
                  </label>
                  <p className="text-xs text-gray-500">
                    Detect cellphone/camera etc in the view of the quiz
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border rounded-md bg-gray-50">
            <h3 className="font-medium mb-4">Snapshot Settings</h3>

            <div className="space-y-3">
              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_PERIODIC_SNAPSHOTS"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Periodic Snapshots
                  </label>
                  <p className="text-xs text-gray-500">
                    Take snapshots at regular intervals
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Controller
                  name="proctoringSettings.PROCTORING_RED_FLAG_SNAPSHOTS"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      className="mt-1"
                      checked={field.value || false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked as boolean)
                      }
                    />
                  )}
                />
                <div className="ml-2">
                  <label className="block font-medium text-sm">
                    Red Flag Snapshots
                  </label>
                  <p className="text-xs text-gray-500">
                    Take snapshots when suspicious activity is detected
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border border-blue-200 bg-blue-50 rounded-md flex items-start">
            <Info className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Candidates will be informed about proctoring before starting the
              panda quiz and will need to grant necessary permissions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ProctoringSettingsEditor);
